import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCompanyOnboardingStatusFromState } from '../states/slices/globalSlice';
import useRoute from './routesHook';
import { processOnboardingTasks, calculateTotalTasks, ActionTask } from '../utils/onboardingTasksUtils';

/**
 * Custom hook to manage onboarding tasks
 * Centralizes all task processing logic and provides computed values
 */
export const useOnboardingTasks = () => {
  const onboardingStatus = useSelector(getCompanyOnboardingStatusFromState);
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();

  // Process all tasks with memoization
  const tasks = useMemo<ActionTask[]>(() => {
    return processOnboardingTasks(onboardingStatus, navigate, getDashboardBase);
  }, [
    onboardingStatus?.securitySettings,
    onboardingStatus?.notificationSettings,
    // onboardingStatus?.payoutAccount,
    onboardingStatus?.insuranceCompanyConfig,
    onboardingStatus?.policyData,
    onboardingStatus?.team,
    onboardingStatus?.companyInfo,
    onboardingStatus?.pendingActions,
    navigate,
    getDashboardBase
  ]);

  // Calculate total tasks with memoization
  const totalTasks = useMemo(() => {
    return calculateTotalTasks(onboardingStatus);
  }, [
    onboardingStatus?.securitySettings,
    onboardingStatus?.notificationSettings,
    // onboardingStatus?.payoutAccount,
    onboardingStatus?.insuranceCompanyConfig,
    onboardingStatus?.policyData,
    onboardingStatus?.team,
    onboardingStatus?.companyInfo,
    onboardingStatus?.pendingActions
  ]);

  // Computed values
  const completedTasks = useMemo(() => {
    return tasks.filter(t => t.completed).length;
  }, [tasks]);

  const incompleteTasks = useMemo(() => {
    return tasks.filter(t => !t.completed).length;
  }, [tasks]);

  // const completionPercentage = onboardingStatus?.completionPercentage || 0;
  // const isOnboardingComplete = onboardingStatus?.onboardingComplete || false;

  // Calculate completion percentage based on actual displayed tasks
  const completionPercentage = useMemo(() => {
    if (totalTasks === 0) return 0;
    return Math.round((completedTasks / totalTasks) * 100);
  }, [completedTasks, totalTasks]);

  const isOnboardingComplete = useMemo(() => {
    return totalTasks > 0 && completedTasks === totalTasks;
  }, [completedTasks, totalTasks]);

  return {
    tasks,
    totalTasks,
    completedTasks,
    incompleteTasks,
    completionPercentage,
    isOnboardingComplete,
    onboardingStatus
  };
};
